import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import React, { Fragment, useEffect, useState } from "react";
import { createNewUser, fetchUserList, updateUser } from "../actions/user";
import { useNavigate, useParams } from "react-router-dom";

import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import InputField from "../components/Admin/Reusable/InputField";
import KeyIcon from "@mui/icons-material/Key";
import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import { basicFormValidationHandler } from "../utils";
import { fetchCountryFormList } from "../actions/country";
import { fetchRoleList } from "../actions/role";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FormSystemInformation from "../components/Admin/Reusable/FormSystemInformation";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";

function generateRandomPassword() {
  const length = Math.floor(Math.random() * 3) + 13; // Dužina između 9 i 11 karaktera
  const specialChars = "-_~.#!@&";

  const allChars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789" +
    specialChars;

  const getRandomChar = (charSet) => {
    const randomIndex = Math.floor(Math.random() * charSet.length);
    return charSet.charAt(randomIndex);
  };

  let password = "";
  password += getRandomChar("ABCDEFGHIJKLMNOPQRSTUVWXYZ"); // Prvo slovo je veliko
  password += getRandomChar("0123456789"); // Barem jedan broj
  password += getRandomChar(specialChars); // Barem jedan specijalni karakter

  while (password.length < length) {
    password += getRandomChar(allChars);
  }

  return password;
}

const initialState = {
  name: "",
  lastName: "",
  email: "",
  roleId: "",
  countryId: "",
  accountType: "SUPER_ADMIN",
  password: "",
  status: "ACTIVE",
};

const UserAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const { id } = useParams();
  const { t } = useTranslation();

  const { countryFormList } = useSelector((state) => state.countryReducer);
  const { roleList } = useSelector((state) => state.roleReducer);
  const { userDetail } = useSelector((state) => state.userReducer);

  const handleInputChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const translateForm = "usersForm";
  const translateModule = "users";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  //pozvati podatke za drzave kako bi izlistali u selektu
  useEffect(() => {
    dispatch(fetchCountryFormList());
    dispatch(fetchRoleList());

    setData(initialState);
  }, [dispatch]);

  useEffect(() => {
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchUserList(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (userDetail && id) {
      setData((prevData) => ({
        ...prevData,
        name: userDetail.name || "",
        lastName: userDetail.lastName || "",
        email: userDetail.email || "",
        roleId: userDetail.roleId || "",
        countryId: userDetail.countryId || "",
        accountType: userDetail.accountType || "",
        status: userDetail.status || "ACTIVATE",
      }));
    }
  }, [userDetail, id]);

  const clientValidationHandler = () => {
    const { name, lastName, email, countryId, roleId, password } = data;

    let errorsObject = {};

    errorsObject = basicFormValidationHandler({
      name,
      lastName,
      email,
      countryId,
      roleId,
      ...(!id && { password }),
    });

    setErrors(errorsObject);

    if (!isEmpty(errorsObject))
      toast.error("Morate popuniti sva obavezna polja", { theme: "colored" });
    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (clientValidationHandler()) {
      return;
    }

    if (id) {
      data.id = id;
      dispatch(updateUser(data, navigate));
    } else {
      dispatch(createNewUser(data, navigate));
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setData((prevUser) => ({
      ...prevUser,
      password: newPassword,
    }));
  };

  return (
    <Fragment>
      <Breadcrumb
        type={translateModule}
        id={id}
        pageType={pageType}
        breadcrumbItems={breadcrumbItems}
      />
      <Container>
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={9}>
                  <Card>
                    <Card.Header>
                      <FormTitleMain />
                    </Card.Header>
                    <Card.Body>
                      <div className="product-holder-admin-left-side-text">
                        <Row>
                          <Col lg={4} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t(`${translateForm}.name`)}
                              name="name"
                              value={data.name}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                              required
                            />
                          </Col>
                          <Col lg={4} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t(`${translateForm}.lastName`)}
                              name="lastName"
                              value={data.lastName}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                              required
                            />
                          </Col>
                          <Col lg={4} md={12} xs={12} className="mb-4">
                            <FormControl
                              fullWidth
                              required
                              size="small"
                              error={errors?.countryId}
                            >
                              <InputLabel id="demo-simple-select-label">
                                {t(`${translateForm}.country`)}
                              </InputLabel>
                              <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={data.countryId}
                                label={t(`${translateForm}.country`)}
                                name="countryId"
                                onChange={handleInputChange}
                                size="small"
                              >
                                {countryFormList?.map((item, key) => (
                                  <MenuItem value={item.id} key={key}>
                                    {item.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors?.countryId && (
                                <FormHelperText>obavezno polje</FormHelperText>
                              )}
                            </FormControl>
                          </Col>
                          <Col lg={6} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t(`${translateForm}.email`)}
                              name="email"
                              value={data.email}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                              required
                            />
                          </Col>
                          <Col lg={6} md={12} xs={12} className="mb-4">
                            <FormControl fullWidth requeired size="small">
                              <InputLabel id="demo-simple-select-label">
                                {t(`${translateForm}.accountType`)}
                              </InputLabel>
                              <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={data.accountType}
                                label={t(`${translateForm}.accountType`)}
                                name="accountType"
                                onChange={handleInputChange}
                                size="small"
                              >
                                <MenuItem value="SUPER_ADMIN">
                                  {t(`${translateForm}.mainAdmin`)}
                                </MenuItem>
                                <MenuItem value="COUNTRY_ADMIN">
                                  {t(`${translateForm}.countryAdmin`)}
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Col>
                          {!id && (
                            <Fragment>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <Button
                                  className="generate-password-input w-100"
                                  variant="contained"
                                  color="primary"
                                  startIcon={<KeyIcon />}
                                  onClick={() => handleGeneratePassword()}
                                >
                                  {t(`${translateForm}.passwordTitle`)}
                                </Button>
                              </Col>
                              <Col lg={8} md={12} xs={12}>
                                <InputField
                                  label={t(`${translateForm}.password`)}
                                  name="password"
                                  value={data.password}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  disabled
                                  required
                                />
                              </Col>
                            </Fragment>
                          )}
                        </Row>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <SelectStatus
                        data={data}
                        handleInputChange={handleInputChange}
                      />
                    </Col>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          {t(`${translateForm}.roleTitle`)}
                        </Card.Header>
                        <Card.Body>
                          <FormControl
                            fullWidth
                            required
                            size="small"
                            error={errors?.roleId}
                            className="mb-2"
                          >
                            <InputLabel id="demo-simple-select-label">
                              {t(`${translateForm}.role`)}
                            </InputLabel>
                            <Select
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={data.roleId}
                              label={t(`${translateForm}.role`)}
                              name="roleId"
                              onChange={handleInputChange}
                              size="small"
                            >
                              {roleList?.map((item, key) => (
                                <MenuItem value={item.id} key={key}>
                                  {item.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors?.roleId && (
                              <FormHelperText>obavezno polje</FormHelperText>
                            )}
                          </FormControl>
                        </Card.Body>
                      </Card>
                    </Col>
                    {id && (
                      <Col lg={12}>
                        <FormSystemInformation data={data} />
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default UserAdd;
